import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { portalEn } from '@/i18n/en';
import { portalTa } from '@/i18n/ta';

export type Lang = 'en' | 'ta';

// Shared with public site — same key so language choice persists across subdomains
const STORAGE_KEY = 'shrineLang';

function resolve(obj: Record<string, unknown>, path: string): string {
  const result = path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object') return (acc as Record<string, unknown>)[key];
    return undefined;
  }, obj);
  return typeof result === 'string' ? result : path;
}

interface LanguageContextType {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggle: () => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: 'en',
  setLang: () => {},
  toggle: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored === 'ta' ? 'ta' : 'en';
    } catch {
      return 'en';
    }
  });

  const setLang = useCallback((l: Lang) => {
    try { localStorage.setItem(STORAGE_KEY, l); } catch { /* noop */ }
    setLangState(l);
  }, []);

  const toggle = useCallback(() => {
    setLang(lang === 'en' ? 'ta' : 'en');
  }, [lang, setLang]);

  const t = useCallback((key: string): string => {
    const dict = (lang === 'ta' ? portalTa : portalEn) as Record<string, unknown>;
    const text = resolve(dict, key);
    // Fallback to English
    if (text === key && lang === 'ta') {
      return resolve(portalEn as Record<string, unknown>, key);
    }
    return text;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => useContext(LanguageContext);
